import { config } from '../config';
import { bboxAround, isValidLatLng, randomPointNear, type LatLng } from '../game/geo';
import { pickPlace, type GameMap } from '../game/maps';
import { prefersLightImages } from './device';
import { allAttemptsFailed, batchSearch, describeError, fetchJson, isFarEnough, nearestPanorama } from './search';
import {
  NoPanoramaFoundError,
  ProviderUnavailableError,
  type FindOptions,
  type NeighborLink,
  type Panorama,
  type PanoramaProvider,
} from './types';

/**
 * Panoramax (https://panoramax.fr): open, federated street-level imagery (mostly France).
 * No key needed. API: STAC (https://docs.panoramax.fr/api/), CORS enabled.
 * Pictures are under open licences (mostly CC-BY-SA 4.0 / Etalab 2.0).
 */

/** Random offset applied around the selected town. */
const JITTER_KM = 14;
/** Half-size of the searched bounding box. */
const SEARCH_HALF_KM = 10;
/** Items requested per search (the API sorts them by distance to the bbox centre). */
const SEARCH_LIMIT = 50;

interface StacLink {
  rel?: string;
  href?: string;
  type?: string;
  id?: string;
  geometry?: { type?: string; coordinates?: [number, number] };
}

interface StacAsset {
  href?: string;
  type?: string;
  roles?: string[];
}

export interface StacItem {
  id?: string;
  geometry?: { type?: string; coordinates?: [number, number] };
  properties?: Record<string, unknown>;
  assets?: Record<string, StacAsset>;
  links?: StacLink[];
  providers?: { name?: string; roles?: string[] }[];
  collection?: string;
}

interface PanoramaxMeta {
  next?: StacLink;
  prev?: StacLink;
}

function viewerBaseUrl(apiUrl: string): string {
  return apiUrl.replace(/\/api\/?$/, '');
}

function linkPosition(link: StacLink | undefined): LatLng | null {
  const c = link?.geometry?.coordinates;
  if (!Array.isArray(c) || c.length < 2) return null;
  const p = { lat: Number(c[1]), lng: Number(c[0]) };
  return isValidLatLng(p) ? p : null;
}

function findAsset(item: StacItem, key: string, role: string): string | undefined {
  const assets = item.assets ?? {};
  const direct = assets[key]?.href;
  if (direct) return direct;
  return Object.values(assets).find((a) => a?.roles?.includes(role) && (a.type ?? 'image/jpeg') === 'image/jpeg')?.href;
}

/** True when the STAC item describes a full 360° equirectangular picture. */
export function isPanoramaxSphere(item: StacItem): boolean {
  const props = item.properties ?? {};
  const orientation = props['pers:interior_orientation'] as { field_of_view?: unknown } | undefined;
  return Number(orientation?.field_of_view) === 360;
}

/** Converts a Panoramax STAC item into a playable panorama, or `null` if unusable. */
export function parsePanoramaxItem(item: StacItem, apiUrl: string = config.panoramaxApiUrl, light = false): Panorama | null {
  if (!item?.id || !isPanoramaxSphere(item)) return null;
  const coords = item.geometry?.coordinates;
  if (!Array.isArray(coords)) return null;
  const position = { lat: Number(coords[1]), lng: Number(coords[0]) };
  if (!isValidLatLng(position)) return null;

  const props = item.properties ?? {};
  const status = props['geovisio:status'];
  if (typeof status === 'string' && status !== 'ready') return null;

  const hd = findAsset(item, 'hd', 'data');
  const sd = findAsset(item, 'sd', 'visual');
  const imageUrls = (light ? [sd, hd] : [hd, sd]).filter((u): u is string => typeof u === 'string' && u.length > 0);
  if (imageUrls.length === 0) return null;

  const links = Array.isArray(item.links) ? item.links : [];
  const isNav = (rel: string) => (l: StacLink) => l?.rel === rel && typeof l.href === 'string';
  const meta: PanoramaxMeta = { next: links.find(isNav('next')), prev: links.find(isNav('prev')) };

  const producer =
    item.providers?.find((p) => p?.roles?.includes('producer') && p.name)?.name ??
    (typeof props['geovisio:producer'] === 'string' ? (props['geovisio:producer'] as string) : undefined);

  return {
    provider: 'panoramax',
    id: item.id,
    position,
    heading: Number(props['view:azimuth']) || 0,
    imageUrls: [...new Set(imageUrls)],
    attribution: {
      sourceName: 'Panoramax',
      author: producer,
      license: typeof props.license === 'string' ? props.license : undefined,
      capturedAt: typeof props.datetime === 'string' ? props.datetime : undefined,
      sourceUrl: `${viewerBaseUrl(apiUrl)}/#focus=pic&pic=${encodeURIComponent(item.id)}`,
    },
    meta,
  };
}

export function createPanoramaxProvider(apiUrl: string = config.panoramaxApiUrl): PanoramaProvider {
  async function loadItem(href: string, signal?: AbortSignal): Promise<Panorama> {
    const item = await fetchJson<StacItem>(href, { signal });
    const panorama = parsePanoramaxItem(item, apiUrl, prefersLightImages());
    if (!panorama) throw new Error('Cette image n’est pas un panorama 360°.');
    return panorama;
  }

  return {
    id: 'panoramax',
    name: 'Panoramax',
    description: 'Géocommun libre et ouvert, sans clé. Images surtout en France, couverture plus limitée ailleurs.',
    viewer: 'sphere',
    mapKind: 'leaflet',
    isConfigured: () => true,
    supportedMaps: ['world', 'europe', 'france'],

    async findRandom(map: GameMap, { signal, exclude, rng = Math.random }: FindOptions): Promise<Panorama> {
      const minDistanceKm = map.scaleKm / 15;
      const light = prefersLightImages();
      const result = await batchSearch({
        signal,
        batches: 4,
        parallel: 3,
        attempt: async (attemptSignal) => {
          const place = pickPlace(map, 'panoramax', rng);
          const center = randomPointNear(place, JITTER_KM, rng);
          const bbox = bboxAround(center, SEARCH_HALF_KM).join(',');
          const data = await fetchJson<{ features?: StacItem[] }>(`${apiUrl}/search?bbox=${bbox}&limit=${SEARCH_LIMIT}`, {
            signal: attemptSignal,
          });
          const candidates = (Array.isArray(data?.features) ? data.features : [])
            .map((f) => parsePanoramaxItem(f, apiUrl, light))
            .filter((p): p is Panorama => p !== null && isFarEnough(p.position, exclude, minDistanceKm));
          if (candidates.length === 0) return null;
          // Results come sorted by distance to the random centre: take one of the closest.
          return candidates[Math.floor(rng() * Math.min(candidates.length, 5))];
        },
      });
      if (result.panorama) return result.panorama;
      if (allAttemptsFailed(result)) {
        throw new ProviderUnavailableError('panoramax', `Panoramax est indisponible : ${describeError(result.errors[0])}.`, result.errors[0]);
      }
      throw new NoPanoramaFoundError('panoramax', 'Aucun panorama 360° Panoramax trouvé dans les zones explorées.');
    },

    async findNearby(target, { radiusKm, excludeId, signal }) {
      const bbox = bboxAround(target, radiusKm).join(',');
      const data = await fetchJson<{ features?: StacItem[] }>(`${apiUrl}/search?bbox=${bbox}&limit=30`, { signal });
      const panoramas = (Array.isArray(data?.features) ? data.features : [])
        .map((f) => parsePanoramaxItem(f, apiUrl, prefersLightImages()))
        .filter((p): p is Panorama => p !== null);
      return nearestPanorama(target, panoramas, excludeId);
    },

    async getNeighbors(panorama: Panorama): Promise<NeighborLink[]> {
      const meta = (panorama.meta ?? {}) as PanoramaxMeta;
      const result: NeighborLink[] = [];
      for (const link of [meta.next, meta.prev]) {
        const position = linkPosition(link);
        if (!link?.href || !position) continue;
        const href = link.href;
        result.push({ id: link.id ?? href, position, load: (signal) => loadItem(href, signal) });
      }
      return result;
    },
  };
}
